// Session Persistence utilities using localStorage
// Handles persisting AI analysis state and results across tab switches

export interface AnalysisSession {
  ideaId: string;
  analysisType: 'standard' | 'deep';
  isAnalyzing: boolean;
  analysisResult?: string;
  analysisError?: string;
  startTime: number;
  lastUpdate: number;
}

export interface FormSession {
  ideaId?: string;
  taskId?: string;
  formData?: Record<string, any>;
  timestamp: number;
}

const ANALYSIS_SESSION_KEY = 'ideahopper_analysis_session';
const FORM_SESSION_KEY = 'ideahopper_form_session';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

// Analysis Session Management
export const saveAnalysisSession = (session: AnalysisSession) => {
  try {
    const sessionData = {
      ...session,
      lastUpdate: Date.now()
    };
    localStorage.setItem(ANALYSIS_SESSION_KEY, JSON.stringify(sessionData));
  } catch (error) {
    }
};

export const getAnalysisSession = (): AnalysisSession | null => {
  try {
    const stored = localStorage.getItem(ANALYSIS_SESSION_KEY);
    if (!stored) return null;

    const session: AnalysisSession = JSON.parse(stored);
    
    // Check if session is expired
    const now = Date.now();
    if (now - session.lastUpdate > SESSION_TIMEOUT) {
      clearAnalysisSession();
      return null;
    }

    return session;
  } catch (error) {
    return null;
  }
};

export const updateAnalysisSession = (updates: Partial<AnalysisSession>) => {
  const currentSession = getAnalysisSession();
  if (!currentSession) return;

  const updatedSession = {
    ...currentSession,
    ...updates,
    lastUpdate: Date.now()
  };

  saveAnalysisSession(updatedSession);
};

export const clearAnalysisSession = () => {
  try {
    localStorage.removeItem(ANALYSIS_SESSION_KEY);
  } catch (error) {
    }
};

// Form Session Management
export const saveFormSession = (session: FormSession) => {
  try {
    const sessionData = {
      ...session,
      timestamp: Date.now()
    };
    localStorage.setItem(FORM_SESSION_KEY, JSON.stringify(sessionData));
  } catch (error) {
    }
};

export const getFormSession = (): FormSession | null => {
  try {
    const stored = localStorage.getItem(FORM_SESSION_KEY);
    if (!stored) return null;

    const session: FormSession = JSON.parse(stored);
    
    // Check if session is expired
    const now = Date.now();
    if (now - session.timestamp > SESSION_TIMEOUT) {
      clearFormSession();
      return null;
    }

    return session;
  } catch (error) {
    return null;
  }
};

export const clearFormSession = () => {
  try {
    localStorage.removeItem(FORM_SESSION_KEY);
  } catch (error) {
    }
};

// General session management
export const clearAllSessions = () => {
  clearAnalysisSession();
  clearFormSession();
};

// Check if browser supports localStorage
export const isLocalStorageAvailable = (): boolean => {
  try {
    const test = 'test';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
};

// Recovery helpers
export const shouldRecoverAnalysis = (ideaId: string): boolean => {
  const session = getAnalysisSession();
  return session?.ideaId === ideaId && session.isAnalyzing;
};

export const getRecoveryData = (ideaId: string) => {
  const session = getAnalysisSession();
  if (!session || session.ideaId !== ideaId) return null;

  return {
    isAnalyzing: session.isAnalyzing,
    analysisType: session.analysisType,
    analysisResult: session.analysisResult,
    analysisError: session.analysisError,
    duration: Date.now() - session.startTime
  };
}; 